package com.terapia.terasenior.ui.patient

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import com.terapia.terasenior.domain.model.patient.Patient
import com.terapia.terasenior.domain.model.patient.PatientStatus
import com.terapia.terasenior.util.DateUtils

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun EditPatientDialog(
    patient: Patient,
    onDismiss: () -> Unit,
    onConfirm: (Patient) -> Unit,
    isLoading: Boolean
) {
    val layout = com.terapia.terasenior.ui.components.LocalTeraseniorLayout.current
    var externalId by remember { mutableStateOf(patient.externalId ?: "") }
    var nif by remember { mutableStateOf(patient.nif ?: "") }
    var firstName by remember { mutableStateOf(patient.firstName) }
    var lastName by remember { mutableStateOf(patient.lastName) }
    var preferredName by remember { mutableStateOf(patient.preferredName ?: "") }
    var birthDate by remember { mutableStateOf(DateUtils.toUserFormat(patient.birthDate)) }
    var admissionDate by remember { mutableStateOf(DateUtils.toUserFormat(patient.admissionDate)) }
    var dischargeDate by remember { mutableStateOf(DateUtils.toUserFormat(patient.dischargeDate)) }
    var address by remember { mutableStateOf(patient.address ?: "") }
    var city by remember { mutableStateOf(patient.city ?: "") }
    var postalCode by remember { mutableStateOf(patient.postalCode ?: "") }
    var province by remember { mutableStateOf(patient.province ?: "") }
    var phone by remember { mutableStateOf(patient.phone ?: "") }
    var contactName by remember { mutableStateOf(patient.contactName ?: "") }
    var contactPhone by remember { mutableStateOf(patient.contactPhone ?: "") }
    var notes by remember { mutableStateOf(patient.notes ?: "") }
    var status by remember { mutableStateOf(patient.status) }

    var selectedTab by remember { mutableStateOf(0) }

    AlertDialog(
        onDismissRequest = onDismiss,
        modifier = Modifier
            .fillMaxWidth(if (layout.isCompactWidth) 0.96f else 0.9f)
            .widthIn(max = 760.dp)
            .heightIn(max = if (layout.isCompactHeight) 620.dp else 720.dp),
        properties = DialogProperties(usePlatformDefaultWidth = false),
        title = { Text("Edición de Ficha Clínica", style = MaterialTheme.typography.headlineSmall) },
        text = {
            Column(modifier = Modifier.fillMaxWidth().heightIn(max = if (layout.isCompactHeight) 430.dp else 520.dp)) {
                
                TabRow(selectedTabIndex = selectedTab, modifier = Modifier.padding(bottom = 16.dp)) {
                    Tab(selected = selectedTab == 0, onClick = { selectedTab = 0 }, text = { Text("Gestión") })
                    Tab(selected = selectedTab == 1, onClick = { selectedTab = 1 }, text = { Text("Ficha") })
                    Tab(selected = selectedTab == 2, onClick = { selectedTab = 2 }, text = { Text("Contacto") })
                    Tab(selected = selectedTab == 3, onClick = { selectedTab = 3 }, text = { Text("Notas") })
                }

                Column(modifier = Modifier.weight(1f).verticalScroll(rememberScrollState()), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                    when (selectedTab) {
                        0 -> {
                            OutlinedTextField(value = externalId, onValueChange = { externalId = it }, label = { Text("Nº Expediente / ID Interno") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            OutlinedTextField(value = nif, onValueChange = { nif = it }, label = { Text("DNI / NIF Oficial") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            OutlinedTextField(value = admissionDate, onValueChange = { admissionDate = it }, label = { Text("Fecha Alta (DD-MM-AAAA)") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            OutlinedTextField(value = dischargeDate, onValueChange = { dischargeDate = it }, label = { Text("Fecha Baja (DD-MM-AAAA)") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            
                            Text("Estado Actual", style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold)
                            FlowRow(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                PatientStatus.entries.forEach { s ->
                                    val label = when(s) {
                                        PatientStatus.ACTIVE -> "Activo"
                                        PatientStatus.INACTIVE -> "Inactivo"
                                        PatientStatus.BAJA -> "Baja"
                                        PatientStatus.DISCHARGED -> "Alta"
                                    }
                                    FilterChip(selected = status == s, onClick = { status = s }, label = { Text(label) })
                                }
                            }
                        }
                        1 -> {
                            OutlinedTextField(value = firstName, onValueChange = { firstName = it }, label = { Text("Nombre *") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next), isError = firstName.isBlank())
                            OutlinedTextField(value = lastName, onValueChange = { lastName = it }, label = { Text("Apellidos *") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next), isError = lastName.isBlank())
                            OutlinedTextField(value = preferredName, onValueChange = { preferredName = it }, label = { Text("Nombre Preferido") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            OutlinedTextField(value = birthDate, onValueChange = { birthDate = it }, label = { Text("Fecha Nacimiento (DD-MM-AAAA)") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                        }
                        2 -> {
                            OutlinedTextField(value = address, onValueChange = { address = it }, label = { Text("Dirección") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                OutlinedTextField(value = city, onValueChange = { city = it }, label = { Text("Población") }, modifier = Modifier.weight(1f), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                                OutlinedTextField(value = postalCode, onValueChange = { postalCode = it }, label = { Text("CP") }, modifier = Modifier.width(100.dp), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            }
                            OutlinedTextField(value = province, onValueChange = { province = it }, label = { Text("Provincia") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            OutlinedTextField(value = phone, onValueChange = { phone = it }, label = { Text("Teléfono Principal") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                        }
                        3 -> {
                            Text("Referente de Emergencia", style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold)
                            OutlinedTextField(value = contactName, onValueChange = { contactName = it }, label = { Text("Nombre Familiar") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            OutlinedTextField(value = contactPhone, onValueChange = { contactPhone = it }, label = { Text("Teléfono Familiar") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next))
                            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
                            Text("Observaciones del Terapeuta", style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold)
                            OutlinedTextField(
                                value = notes, 
                                onValueChange = { notes = it }, 
                                label = { Text("Notas de Seguimiento") }, 
                                modifier = Modifier.fillMaxWidth().height(150.dp),
                                shape = RoundedCornerShape(12.dp)
                            )
                        }
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = { 
                    onConfirm(patient.copy(
                        externalId = externalId, nif = nif, firstName = firstName, lastName = lastName, 
                        preferredName = preferredName, 
                        birthDate = DateUtils.toDbFormat(birthDate), 
                        admissionDate = DateUtils.toDbFormat(admissionDate),
                        dischargeDate = DateUtils.toDbFormat(dischargeDate), 
                        address = address, city = city, postalCode = postalCode,
                        province = province, phone = phone, contactName = contactName, 
                        contactPhone = contactPhone, notes = notes, status = status
                    ))
                },
                enabled = firstName.isNotBlank() && lastName.isNotBlank() && !isLoading,
                shape = RoundedCornerShape(12.dp)
            ) {
                if (isLoading) CircularProgressIndicator(modifier = Modifier.size(20.dp)) else Text("Guardar Cambios")
            }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancelar") } },
        shape = RoundedCornerShape(24.dp)
    )
}
